import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@/app/services/api.service';
import { NotificationService } from '@/app/services/notification.service';
import { handleApiError } from '@/app/utils/api-error-handler';
import * as game from '@/lib-backend/methods/game';
import { AdminGame, FieldUpdate } from '@/lib-backend';

/**
 * The art slots a finalized-art variant can write onto a game's per-realm
 * overrides. `undefined` leaves the field untouched; an explicit value (incl.
 * `null`) sets/clears that single override.
 */
export interface ArtOverrides {
    iconUrl?: string | null;
    bannerUrl?: string | null;
    images?: string[] | null;
}

export type CreateGameResult =
    | { kind: 'ok'; game: AdminGame }
    | { kind: 'error'; reason: game.CreateGameResult };

export type UpdateGameResult =
    | { kind: 'ok'; game: AdminGame }
    | { kind: 'error'; reason: game.UpdateGameResult };

@Injectable({ providedIn: 'root' })
export class RealmGamesService {
    private readonly api = inject(ApiService);
    private readonly router = inject(Router);
    private readonly notification = inject(NotificationService);

    readonly games = signal<AdminGame[]>([]);
    readonly loading = signal(false);
    readonly error = signal<string | null>(null);

    async loadGames(realmId: string): Promise<void> {
        this.loading.set(true);
        this.error.set(null);
        try {
            const resp = await this.api.client.call(game.listGames({ RealmId: realmId }));
            this.games.set(resp.Games);
        } catch (e) {
            this.error.set('Failed to load games.');
            handleApiError(e, this.router, this.notification);
        } finally {
            this.loading.set(false);
        }
    }

    async createGame(params: game.CreateGameRequest): Promise<CreateGameResult> {
        try {
            const resp = await this.api.client.call(game.createGame(params));
            if (resp.Result === 'Ok') {
                this.games.update((list) => [...list, resp.Game]);
                this.notification.show({ severity: 'success', summary: 'Game created' });
                return { kind: 'ok', game: resp.Game };
            }
            return { kind: 'error', reason: resp.Result };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { kind: 'error', reason: 'Unknown' };
        }
    }

    async updateGame(params: game.UpdateGameRequest): Promise<UpdateGameResult> {
        try {
            const resp = await this.api.client.call(game.updateGame(params));
            if (resp.Result === 'Ok') {
                this.games.update((list) =>
                    list.map((g) => (g.Id === resp.Game.Id ? resp.Game : g))
                );
                this.notification.show({ severity: 'success', summary: 'Game updated' });
                return { kind: 'ok', game: resp.Game };
            }
            return { kind: 'error', reason: resp.Result };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { kind: 'error', reason: 'Unknown' };
        }
    }

    /**
     * Write a chosen finalized-art variant's slots into one game's per-realm
     * overrides. Only the three art fields are touched; every other override
     * (Title, IsActive, …) is left as-is. This is the finalized-art "Select"
     * action — the cache is shared across realms, `AdminGameOverrides` is where
     * the per-realm pick lands.
     */
    async applyArtOverrides(
        realmId: string,
        gameId: string,
        art: ArtOverrides,
    ): Promise<UpdateGameResult> {
        const set = <T>(v: T | undefined): FieldUpdate<T> =>
            v === undefined ? null : { value: v };
        return this.updateGame({
            RealmId: realmId,
            GameId: gameId,
            Title: null,
            Description: null,
            BannerUrl: set(art.bannerUrl),
            IconUrl: set(art.iconUrl),
            Images: set(art.images),
            IsActive: null,
            IsFeatured: null,
        });
    }

    clear(): void {
        this.games.set([]);
        this.error.set(null);
    }
}
